import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { AppButton, AppInput } from "@libs/zapi-ui/custom";
import { MarkdownView } from "@/zapi-admin/shared/components/MarkdownView/MarkdownView";
import { useDebouncedValue } from "@/zapi-admin/shared/hooks/useDebouncedValue";
import { parseTranscript, type TTimelineItem } from "../lib/transcript";
import { formatClock, formatCost, formatDelta, formatDuration } from "../lib/format";

const RESULT_PREVIEW_CHARS = 4000;
const MAX_ITEMS = 5000;

type TFilters = { text: boolean; tools: boolean; thinking: boolean; other: boolean };

const itemText = (item: TTimelineItem): string => {
  switch (item.kind) {
    case "text":
    case "thinking":
      return item.text;
    case "tool":
      return `${item.name} ${item.summary} ${JSON.stringify(item.input ?? "")} ${item.result?.text ?? ""}`;
    case "result":
      return item.text ?? "";
    case "init":
      return `${item.model ?? ""} ${item.tools.join(" ")}`;
    default:
      return JSON.stringify(item.raw);
  }
};

const isVisible = (item: TTimelineItem, filters: TFilters) => {
  if (item.kind === "text") return filters.text;
  if (item.kind === "tool") return filters.tools;
  if (item.kind === "thinking") return filters.thinking;
  if (item.kind === "other") return filters.other;
  return true;
};

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-1.5 text-[13px] text-zinc-700 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-zinc-900"
      />
      {label}
    </label>
  );
}

function Pre({ text, className = "" }: { text: string; className?: string }) {
  const [full, setFull] = useState(false);
  const truncated = !full && text.length > RESULT_PREVIEW_CHARS;
  return (
    <div>
      <pre className={`whitespace-pre-wrap wrap-break-word text-[12px] leading-[18px] font-mono ${className}`}>
        {truncated ? text.slice(0, RESULT_PREVIEW_CHARS) : text}
      </pre>
      {truncated && (
        <AppButton appearance="text" color="zinc950" size="sm" className="px-1" onClick={() => setFull(true)}>
          Show all {text.length.toLocaleString()} characters
        </AppButton>
      )}
    </div>
  );
}

function Step({
  item,
  previousTs,
  expanded,
  onToggle
}: {
  item: TTimelineItem;
  previousTs: string | null;
  expanded: boolean;
  onToggle: () => void;
}) {
  const subagent = "subagent" in item && item.subagent;
  const clock = (
    <div className="w-[150px] shrink-0 text-[12px] text-zinc-500 font-mono pt-[3px]">
      {formatClock(item.ts)} <span className="text-zinc-400">{formatDelta(previousTs, item.ts)}</span>
    </div>
  );
  const tag = subagent && (
    <span className="ml-2 rounded-[8px] bg-violet-50 px-1.5 py-px text-[11px] text-violet-700">subagent</span>
  );

  if (item.kind === "init") {
    return (
      <div className="flex gap-3 py-2 border-b border-zinc-100">
        {clock}
        <div className="text-[13px] text-zinc-700">
          Session started · model <span className="font-mono">{item.model ?? "?"}</span> · {item.tools.length} tools
          {item.sessionId && <span className="text-zinc-400 font-mono"> · {item.sessionId}</span>}
        </div>
      </div>
    );
  }

  if (item.kind === "text") {
    return (
      <div className={`flex gap-3 py-2 border-b border-zinc-100 ${subagent ? "pl-6" : ""}`}>
        {clock}
        <div className="min-w-0 flex-1">
          {tag}
          <MarkdownView content={item.text} />
        </div>
      </div>
    );
  }

  if (item.kind === "thinking") {
    return (
      <div className={`flex gap-3 py-2 border-b border-zinc-100 ${subagent ? "pl-6" : ""}`}>
        {clock}
        <div className="min-w-0 flex-1">
          <button type="button" onClick={onToggle} className="flex items-center gap-1 text-[13px] text-zinc-500 italic">
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Thinking ({item.text.length.toLocaleString()} chars){tag}
          </button>
          {expanded && <Pre text={item.text} className="text-zinc-500 mt-1" />}
        </div>
      </div>
    );
  }

  if (item.kind === "tool") {
    const outcome = item.result ? (item.result.isError ? "error" : "ok") : "no result";
    const outcomeClass =
      outcome === "error"
        ? "text-red-700 bg-red-50"
        : outcome === "ok"
        ? "text-green-800 bg-green-50"
        : "text-zinc-500 bg-zinc-100";
    return (
      <div className={`flex gap-3 py-2 border-b border-zinc-100 ${subagent ? "pl-6" : ""}`}>
        {clock}
        <div className="min-w-0 flex-1">
          <button type="button" onClick={onToggle} className="flex items-center gap-2 text-left w-full">
            {expanded ? (
              <ChevronDown size={14} className="shrink-0" />
            ) : (
              <ChevronRight size={14} className="shrink-0" />
            )}
            <span className="font-mono text-[13px] font-semibold text-zinc-900">{item.name}</span>
            <span className="text-[13px] text-zinc-700 truncate">{item.summary}</span>
            <span className={`ml-auto shrink-0 rounded-[8px] px-1.5 py-px text-[11px] ${outcomeClass}`}>
              {outcome}
              {item.result?.ts && ` · ${formatDelta(item.ts, item.result.ts).replace("+", "")}`}
            </span>
            {tag}
          </button>
          {expanded && (
            <div className="mt-2 grid gap-2">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-zinc-400 mb-1">Input</div>
                <Pre text={JSON.stringify(item.input ?? {}, null, 2)} className="bg-zinc-50 rounded-[8px] p-2" />
              </div>
              {item.result && (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-zinc-400 mb-1">Result</div>
                  <Pre
                    text={item.result.text || "(empty)"}
                    className={`rounded-[8px] p-2 ${item.result.isError ? "bg-red-50 text-red-800" : "bg-zinc-50"}`}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (item.kind === "result") {
    const usage = item.usage && typeof item.usage === "object" ? (item.usage as Record<string, unknown>) : null;
    const tokens = usage
      ? ["input_tokens", "output_tokens", "cache_read_input_tokens", "cache_creation_input_tokens"]
          .filter((key) => typeof usage[key] === "number")
          .map((key) => `${key.replace(/_tokens$/, "").replace(/_/g, " ")} ${(usage[key] as number).toLocaleString()}`)
          .join(" · ")
      : "";
    return (
      <div className={`flex gap-3 py-3 rounded-[10px] px-2 mt-2 ${item.isError ? "bg-red-50" : "bg-zinc-50"}`}>
        {clock}
        <div className="min-w-0 flex-1 text-[13px] text-zinc-800">
          <div className="font-semibold">
            Run {item.isError ? "failed" : "finished"}
            {item.subtype && <span className="font-normal text-zinc-500"> · {item.subtype}</span>}
            <span className="font-normal text-zinc-500">
              {" "}
              · {formatDuration(item.durationMs)} · {item.numTurns ?? "?"} turns · {formatCost(item.costUsd)}
            </span>
          </div>
          {tokens && <div className="text-zinc-500 text-[12px] mt-1">{tokens}</div>}
          {item.text && (
            <div className="mt-2">
              <MarkdownView content={item.text} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3 py-2 border-b border-zinc-100">
      {clock}
      <div className="min-w-0 flex-1">
        <button type="button" onClick={onToggle} className="flex items-center gap-1 text-[13px] text-zinc-500">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="font-mono">{item.type}</span> event
        </button>
        {expanded && <Pre text={JSON.stringify(item.raw, null, 2)} className="bg-zinc-50 rounded-[8px] p-2 mt-1" />}
      </div>
    </div>
  );
}

export function RunTimeline({ text }: { text: string }) {
  const transcript = useMemo(() => parseTranscript(text), [text]);
  const [search, setSearch] = useState("");
  const query = useDebouncedValue(search, 250).trim().toLowerCase();
  const [filters, setFilters] = useState<TFilters>({ text: true, tools: true, thinking: false, other: false });
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [allExpanded, setAllExpanded] = useState(false);

  const toggle = (index: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  const isExpanded = (index: number) => (allExpanded ? !expanded.has(index) : expanded.has(index));

  const visible = useMemo(() => {
    const rows: { item: TTimelineItem; index: number }[] = [];
    transcript.items.forEach((item, index) => {
      if (!isVisible(item, filters)) return;
      if (query && !itemText(item).toLowerCase().includes(query)) return;
      rows.push({ item, index });
    });
    return rows;
  }, [transcript, filters, query]);

  const toolCount = transcript.items.filter((i) => i.kind === "tool").length;
  const init = transcript.items.find((i) => i.kind === "init");

  return (
    <div data-testid="run-timeline">
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="w-[300px]">
          <AppInput
            placeholder="Search steps"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-[8px] bg-innoveraZinc-50"
            data-testid="run-timeline-search"
          />
        </div>
        <Toggle label="Text" checked={filters.text} onChange={(v) => setFilters({ ...filters, text: v })} />
        <Toggle label="Tools" checked={filters.tools} onChange={(v) => setFilters({ ...filters, tools: v })} />
        <Toggle label="Thinking" checked={filters.thinking} onChange={(v) => setFilters({ ...filters, thinking: v })} />
        <Toggle label="Other events" checked={filters.other} onChange={(v) => setFilters({ ...filters, other: v })} />
        <AppButton
          appearance="text"
          color="zinc950"
          size="sm"
          className="ml-auto"
          onClick={() => {
            setAllExpanded(!allExpanded);
            setExpanded(new Set());
          }}
        >
          {allExpanded ? "Collapse all" : "Expand all"}
        </AppButton>
      </div>
      <div className="text-[12px] text-zinc-500 mb-2">
        {transcript.items.length.toLocaleString()} steps · {toolCount.toLocaleString()} tool calls
        {init?.kind === "init" && init.model && <span> · model {init.model}</span>}
        {transcript.startedAt && transcript.endedAt && (
          <span> · {formatDelta(transcript.startedAt, transcript.endedAt).replace("+", "")} of activity</span>
        )}
        {transcript.unreadableLines > 0 && <span> · {transcript.unreadableLines} unreadable lines</span>}
        {visible.length !== transcript.items.length && <span> · showing {visible.length.toLocaleString()}</span>}
      </div>
      <div className="border-t border-zinc-100">
        {visible.slice(0, MAX_ITEMS).map(({ item, index }, position) => (
          <Step
            key={index}
            item={item}
            previousTs={position > 0 ? visible[position - 1].item.ts : transcript.startedAt}
            expanded={isExpanded(index)}
            onToggle={() => toggle(index)}
          />
        ))}
        {visible.length > MAX_ITEMS && (
          <div className="py-3 text-[13px] text-zinc-500">
            Only the first {MAX_ITEMS.toLocaleString()} steps are shown; download the zip for the rest.
          </div>
        )}
        {visible.length === 0 && <div className="py-6 text-[13px] text-zinc-500">No steps match.</div>}
      </div>
    </div>
  );
}
